import { Box, Typography, Stack, Table, TableHead, TableRow, TableCell, TableBody, IconButton, TextField, InputAdornment } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import ModernApiEndpoint from './ModernApiEndpoint';
import StructuredApiEndpoint from './StructuredApiEndpoint';
import CodeBlock from './CodeBlock';
import FieldDescriptionTable from './FieldDescriptionTable';
import { useState } from 'react';

const MainContent = ({ activeSection }) => {
    const [copiedSandbox, setCopiedSandbox] = useState(false);
    const [copiedProduction, setCopiedProduction] = useState(false);

    const copyToClipboard = async (text, setter) => {
        try {
            await navigator.clipboard.writeText(text);
            setter(true);
            setTimeout(() => setter(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const createOrderFieldDescriptions = [
        { field: "orders", type: "array", required: true, description: "Array of orders to place." },
        { field: "orders[].customerId", type: "long", required: true, description: "Customer ID for the order." },
        { field: "orders[].organisationName", type: "string", required: true, description: "Organization name on the order." },
        { field: "orders[].userId", type: "string", required: true, description: "User initiating the order." },
        { field: "orders[].userName", type: "string", required: false, description: "Username/email of the requester." },
        { field: "orders[].productDTOList", type: "array", required: true, description: "Products (mailboxes) to create." },
        { field: "orders[].productDTOList[].organisationName", type: "string", required: true, description: "Organization name for the product." },
        { field: "orders[].productDTOList[].emailProvider", type: "string", required: true, description: "Email provider (e.g., MICROSOFT, GOOGLE WORKSPACE)." },
        { field: "orders[].productDTOList[].domain", type: "string", required: true, description: "Domain for the mailbox." },
        { field: "orders[].productDTOList[].email", type: "string", required: true, description: "Mailbox address to create." },
        { field: "orders[].productDTOList[].firstName", type: "string", required: true, description: "First name for mailbox user." },
        { field: "orders[].productDTOList[].lastName", type: "string", required: true, description: "Last name for mailbox user." },
        { field: "orders[].productDTOList[].fwdDomain", type: "string", required: false, description: "Forwarding domain (if any)." },
        { field: "orders[].productDTOList[].preferredPassword", type: "string", required: false, description: "Preferred password for the mailbox." },
        { field: "orders[].productDTOList[].recoveryEmail", type: "string", required: false, description: "Recovery email for mailbox." },
        { field: "orders[].productDTOList[].workspaceAdminFlag", type: "boolean", required: false, description: "Whether mailbox is admin." },
        { field: "connected_apps.domainRegistrar.shareAccessConfirmation", type: "boolean", required: true, description: "Grant direct registrar access." },
        { field: "connected_apps.domainRegistrar.delegateAccessConfirmation", type: "boolean", required: true, description: "Grant delegated registrar access." },
        { field: "connected_apps.domainRegistrar.loginURL", type: "string", required: true, description: "Registrar login URL." },
        { field: "connected_apps.domainRegistrar.username", type: "string", required: true, description: "Registrar username." },
        { field: "connected_apps.domainRegistrar.password", type: "string", required: true, description: "Registrar password." },
        { field: "connected_apps.emailTool.shareAccessConfirmation", type: "boolean", required: true, description: "Grant direct email tool access." },
        { field: "connected_apps.emailTool.delegateAccessConfirmation", type: "boolean", required: true, description: "Grant delegated email tool access." },
        { field: "connected_apps.emailTool.loginURL", type: "string", required: true, description: "Email tool login URL." },
        { field: "connected_apps.emailTool.username", type: "string", required: true, description: "Email tool username." },
        { field: "connected_apps.emailTool.password", type: "string", required: true, description: "Email tool password." },
        { field: "customerOwnsDomain", type: "boolean", required: false, description: "true to skip domain purchase; false for full setup." }
    ];

    const queryParams = [
        { name: "flat", type: "boolean", required: true, description: "Return data in flat format" },
        { name: "customerId", type: "integer", required: true, description: "Customer ID whose orders should be fetched" },
        { name: "page", type: "integer", required: false, description: "Page number for pagination" },
        { name: "size", type: "integer", required: false, description: "Number of records per page" },
        { name: "status", type: "string", required: false, description: "Order status filter" }
    ];

    const createOrderPayload = {
        "orders": [
            {
                "productDTOList": [
                    {
                        "organisationName": "Prashant Org",
                        "emailProvider": "MICROSOFT",
                        "domain": "naveed010.com",
                        "email": "amelia.smith@naveed010.com",
                        "firstName": "Amelia",
                        "lastName": "Smith",
                        "profilePicture": "",
                        "fwdDomain": "naveed010.com",
                        "preferredPassword": "D3aRhkx&FcY&",
                        "recoveryEmail": "info@naveed010.com",
                        "workspaceAdminFlag": false
                    }
                ],
                "customerId": 90,
                "organisationName": "Inboxlogy",
                "userId": "6889aac5c9096436101d0907",
                "userName": "faizan@demandlogy.com"
            }
        ],
        "connected_apps": {
            "domainRegistrar": {
                "shareAccessConfirmation": true,
                "delegateAccessConfirmation": false,
                "loginURL": "https://inboxlogy.com",
                "username": "prashant@demandlogy.com",
                "password": "User@123"
            },
            "emailTool": {
                "shareAccessConfirmation": true,
                "delegateAccessConfirmation": false,
                "loginURL": "https://instantly.ai",
                "username": "test_user@instantly.com",
                "password": "User@123"
            }
        },
        "customerOwnsDomain": false
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'introduction':
                return (
                    <Box>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#1f2937' }}>
                                Welcome to Inboxlogy API
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.8, fontSize: '1.05rem' }}>
                                Comprehensive API documentation for managing domains, workspaces, and email accounts.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: '#eff6ff',
                                borderRadius: '12px',
                                borderLeft: '4px solid #3b82f6',
                                mb: 4
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e40af' }}>
                                Base URLs
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem', mb: 0.5 }}>
                                        Sandbox Environment
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value="https://staging.demandlogy.com/api"
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => copyToClipboard('https://staging.demandlogy.com/api', setCopiedSandbox)}
                                                        size="small"
                                                        sx={{ color: copiedSandbox ? 'primary.main' : '#3b82f6' }}
                                                    >
                                                        {copiedSandbox ? <CheckIcon /> : <CopyIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                fontFamily: 'monospace',
                                                backgroundColor: '#fff',
                                                fontSize: '0.85rem',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#93c5fd'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3b82f6'
                                                }
                                            }
                                        }}
                                        size="small"
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.9rem', mb: 0.5 }}>
                                        Production Environment
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value=" https://app.demandlogy.com/api"
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => copyToClipboard(' https://app.demandlogy.com/api', setCopiedProduction)}
                                                        size="small"
                                                        sx={{ color: copiedProduction ? 'primary.main' : '#3b82f6' }}
                                                    >
                                                        {copiedProduction ? <CheckIcon /> : <CopyIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                fontFamily: 'monospace',
                                                backgroundColor: '#fff',
                                                fontSize: '0.85rem',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#93c5fd'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3b82f6'
                                                }
                                            }
                                        }}
                                        size="small"
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: '#fef3c7',
                                borderRadius: '12px',
                                borderLeft: '4px solid #f59e0b',
                                mb: 4
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#92400e' }}>
                                ⚠️ Important: Development Environment Testing
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#78350f', lineHeight: 1.8, fontSize: '1rem', mb: 2 }}>
                                Before integrating or using the Inboxlogy External Order Placement API, it is strongly recommended to first set up and validate your implementation in a development (dev) environment. This ensures that all configurations—such as domain-related flows, connected apps handling, CSV generation, and billing logic—function as expected across both supported scenarios:
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#78350f', mb: 0.5 }}>
                                    • Full Setup (Inboxlogy purchases the domain)
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#78350f' }}>
                                    • Customer-Owned Domain (No domain purchase)
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ color: '#78350f', lineHeight: 1.8, fontSize: '1rem', mb: 1 }}>
                                Once your end-to-end flow has been fully tested and verified in the dev environment, you may proceed to enable the same configuration in production.
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#78350f', lineHeight: 1.8, fontSize: '1rem', fontWeight: 600 }}>
                                This two-step validation approach helps prevent misconfigurations, incorrect domain purchases, and accidental provisioning on production systems.
                            </Typography>
                        </Box>

                    </Box>
                );

            case 'login':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            Login API
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Authenticate a user with email and password credentials to receive a JWT bearer token.
                            This token must be included in the Authorization header for all subsequent API requests
                            to protected resources.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Authorization
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563' }}>
                                No authentication required for this endpoint. Use the returned token for subsequent requests.
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/login"
                            description="Authenticate a user and receive a JWT token"
                            authorization={{
                                description: "No authentication required for this endpoint. Use the returned token for subsequent requests.",
                                example: "No Authorization header required"
                            }}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    description: "User login credentials",
                                    example: {
                                        "email": "user@example.com",
                                        "password": "password123"
                                    }
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "id": "string",
                                    "customerId": 123456,
                                    "userName": "string",
                                    "firstName": "string",
                                    "lastName": "string",
                                    "token": "string",
                                    "status": 200,
                                    "message": "Login successful"
                                }
                            }]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={JSON.stringify({
                                "email": "user@example.com",
                                "password": "password123"
                            }, null, 2)} />
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/login" \\
     -H "Content-Type: application/json" \\
     -d '{
  "email": "user@example.com",
  "password": "password123"
}'`} />
                            <CodeBlock title="Java Example" language="java" code={`// Using Spring RestTemplate
@Value("${"spring.baseUrl"}")
private String baseUrl;

RestTemplate restTemplate = new RestTemplate();
String url = baseUrl + "/login";

// Create request body
Map<String, String> requestBody = new HashMap<>();
requestBody.put("email", "user@example.com");
requestBody.put("password", "password123");

// Set headers
HttpHeaders headers = new HttpHeaders();
headers.setContentType(MediaType.APPLICATION_JSON);
HttpEntity<Map<String, String>> entity = new HttpEntity<>(requestBody, headers);

// Make POST request
ResponseEntity<LoginResponse> response = restTemplate.postForEntity(
    url, 
    entity, 
    LoginResponse.class
);

// Get token from response
String token = response.getBody().getToken();
System.out.println("Token: " + token);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'signup':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            Signup
                        </Typography>
                        <Box sx={{ mb: 3, p: 3, backgroundColor: '#fef2f2', borderRadius: '12px', borderLeft: '4px solid #ef4444' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#991b1b' }}>
                                App-Only Registration
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#7f1d1d', lineHeight: 1.8 }}>
                                You cannot sign up using the API. Please sign up through the Inboxlogy application interface directly. Once you have an account, you can use the <strong>Login API</strong> to obtain an authentication token.
                            </Typography>
                        </Box>
                    </Box>
                );

            case 'place-order':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Place Order (Domain + Mailboxes)
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Place external orders for mailboxes. Supports both customer-owned domains and Inboxlogy-purchased domains.
                            This is the primary endpoint for creating new infrastructure assignments.
                        </Typography>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/place-an-order"
                            description="Place external mailbox and domain orders"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: createOrderPayload
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "status": 200,
                                    "orderNumber": 3160,
                                    "message": "Order Created Successfully"
                                }
                            }]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={JSON.stringify(createOrderPayload, null, 2)} />
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/place-an-order" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{"orders": [...], "connected_apps": {...}, "customerOwnsDomain": false}'`} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 4, mb: 2, color: '#1f2937' }}>
                                Field Descriptions
                            </Typography>
                            <FieldDescriptionTable fields={createOrderFieldDescriptions} />

                            <CodeBlock title="Java Example" language="java" code={`@Value("${"spring.baseUrl"}")
private String baseUrl;

public void placeOrder() {
    RestTemplate restTemplate = new RestTemplate();
    HttpHeaders headers = new HttpHeaders();
    headers.setContentType(MediaType.APPLICATION_JSON);
    headers.setBearerAuth("YOUR_TOKEN");

    HttpEntity<Object> request = new HttpEntity<>(createOrderPayload, headers);
    ResponseEntity<Map> response = restTemplate.postForEntity(baseUrl + "/place-an-order", request, Map.class);
    
    System.out.println("Response: " + response.getBody());
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'add-mailbox':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Add Mailbox
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Note: Once an order is paid, adding a new mailbox requires placing a new order.
                            The request payload follows the same structure as a primary order.
                        </Typography>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/place-an-order"
                            description="Place a new order to add mailboxes"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: createOrderPayload
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "status": 200,
                                    "orderNumber": 3161,
                                    "message": "Order Created Successfully"
                                }
                            }]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={JSON.stringify(createOrderPayload, null, 2)} />
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/place-an-order" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{"orders": [...], "connected_apps": {...}, "customerOwnsDomain": false}'`} />
                            <CodeBlock title="Java Example" language="java" code={`@Value("\${"spring.baseUrl"}")
private String baseUrl;

public void addMailbox() {
    RestTemplate restTemplate = new RestTemplate();
    HttpHeaders headers = new HttpHeaders();
    headers.setContentType(MediaType.APPLICATION_JSON);
    headers.setBearerAuth("YOUR_TOKEN");

    HttpEntity<Object> request = new HttpEntity<>(createOrderPayload, headers);
    ResponseEntity<Map> response = restTemplate.postForEntity(baseUrl + "/place-an-order", request, Map.class);
    
    System.out.println("Response: " + response.getBody());
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'delete-mailbox':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Cancel Subscription / Delete Mailbox
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Use this endpoint to cancel an active product subscription or delete a mailbox. This will initiate the cancellation process for the specified orders and products.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Note
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.6 }}>
                                To cancel an order, all email accounts associated with that order must be cancelled. Currently, there is no status defined for a “Cancelled Order.”
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/cancelsubscription"
                            description="Cancel an active subscription or product"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: {
                                        "orders": [{ "orderId": "689b0432fea6d83690b19efe" }],
                                        "products": [{ "productId": "689b042cfea6d83690b19a03" }],
                                        "reason": "Client requested cancellation"
                                    }
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "status": 200,
                                    "message": "Subscription Cancelled Successfully"
                                }
                            }]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={JSON.stringify({
                                "orders": [
                                    {
                                        "orderId": "689b0432fea6d83690b19efe"
                                    }
                                ],
                                "products": [
                                    {
                                        "productId": "689b042cfea6d83690b19a03"
                                    }
                                ],
                                "reason": "Client requested cancellation"
                            }, null, 2)} />
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/cancelsubscription" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{
  "orders": [
    {
      "orderId": "689b0432fea6d83690b19efe"
    }
  ],
  "products": [
    {
      "productId": "689b042cfea6d83690b19a03"
    }
  ],
  "reason": "Client requested cancellation"
}'`} />

                            <CodeBlock title="Java Example" language="java" code={`/**
 * Java example using Spring RestTemplate to cancel a subscription
 */
@Value("${"spring.baseUrl"}")
private String baseUrl;

public ResponseEntity<ResponseOrderDTO> cancelSubscription(OrderRef requestPayload) {
    RestTemplate restTemplate = new RestTemplate();
    HttpHeaders headers = new HttpHeaders();
    headers.setContentType(MediaType.APPLICATION_JSON);
    headers.setBearerAuth("YOUR_TOKEN");

    HttpEntity<OrderRef> entity = new HttpEntity<>(requestPayload, headers);
    
    return restTemplate.postForEntity(
        baseUrl + "/cancelsubscription", 
        entity, 
        ResponseOrderDTO.class
    );
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );


            case 'view-orders':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            View Orders API
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Retrieve a comprehensive list of all orders and products for a specific customer. This endpoint
                            supports pagination and filtering by order status. Returns detailed information about each order
                            including domain details, mailbox configurations, and current order status.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Authorization
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
                                A valid <strong>Bearer Token</strong> is required in the Authorization header to fetch user data.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                Example: Authorization: Bearer ********************
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/viewuserorderperproduct"
                            description="Retrieve flat list of product orders for a customer"
                            authorization={{
                                description: "A valid Bearer Token is required in the Authorization header to fetch user data.",
                                example: "Authorization: Bearer ********************"
                            }}
                            headerParams={[
                                {
                                    name: "Authorization",
                                    type: "string",
                                    required: true,
                                    description: "Bearer token for authentication"
                                }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "flatOrders": [
                                        {
                                            "orderId": "ord_1234567890",
                                            "domain": "myoutinstanty.ai",
                                            "email": "ui@myoutinstanty.com",
                                            "status": "COMPLETED",
                                            "platform": "GOOGLE WORKSPACE",
                                            "createdDate": "2025-01-07T10:30:00Z"
                                        }
                                    ],
                                    "totalElements": 1,
                                    "totalPages": 1,
                                    "currentPage": 0
                                }
                            }]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={`// GET request - no body required
// Use query parameters in the URL instead`} />
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/viewuserorderperproduct?flat=true&customerId=891&page=0&size=50&status=All" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                                Query Parameters
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            Parameter
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            Type
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            Required
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            Description
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queryParams.map((param, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{param.name}</TableCell>
                                            <TableCell>{param.type}</TableCell>
                                            <TableCell>{param.required ? "Yes" : "No"}</TableCell>
                                            <TableCell>{param.description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <CodeBlock title="Java Example" language="java" code={`// Using Spring RestTemplate with Bearer Token
@Value("${"spring.baseUrl"}")
private String baseUrl;

RestTemplate restTemplate = new RestTemplate();
String token = "your_jwt_token_here";

// Build URL with query parameters
UriComponentsBuilder builder = UriComponentsBuilder
    .fromHttpUrl(baseUrl + "/viewuserorderperproduct")
    .queryParam("flat", true)
    .queryParam("customerId", 891)
    .queryParam("page", 0)
    .queryParam("size", 50)
    .queryParam("status", "All");

String url = builder.toUriString();

// Set headers with Bearer token
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth(token);
HttpEntity<Void> entity = new HttpEntity<>(headers);

// Make GET request
ResponseEntity<OrderListResponse> response = restTemplate.exchange(
    url,
    HttpMethod.GET,
    entity,
    OrderListResponse.class
);

List<Order> orders = response.getBody().getFlatOrders();
System.out.println("Total Orders: " + orders.size());`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'domain-suggestions':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Suggestions API
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Retrieve up to 50 domain suggestions based on a keyword. This endpoint helps users find
                            available domains related to their target brand or keyword.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/check-domain/{keyword}"
                            description="Retrieve domain suggestions based on a keyword"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "keyword", type: "string", required: true, description: "Keyword to generate suggestions for (Path parameter)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: [
                                    {
                                        "Domain": "keyword-magic.com",
                                        "Available": true,
                                        "PremiumRegistrationPrice": 12.5
                                    }
                                ]
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/check-domain/keyword-magic" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");

HttpEntity<Void> entity = new HttpEntity<>(headers);
ResponseEntity<List> response = restTemplate.exchange(
    baseUrl + "/check-domain/keyword-magic", 
    HttpMethod.GET, 
    entity, 
    List.class
);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'bulk-check':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Bulk Domain Availability Check
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Check the availability and pricing of multiple domains at once.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/check/domain/v2"
                            description="Bulk check domain availability"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "domain", type: "string", required: true, description: "Domain name to check (e.g., woodpecker.com). Multiple domains can be passed as separate 'domain' parameters." }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "status": 200,
                                    "data": [
                                        {
                                            "domain": "woodpecker.com",
                                            "price": 12.5,
                                            "available": true
                                        }
                                    ]
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/check/domain/v2?domain=woodpecker.com" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");

UriComponentsBuilder builder = UriComponentsBuilder
    .fromHttpUrl(baseUrl + "/check/domain/v2")
    .queryParam("domain", "woodpecker.com");

HttpEntity<Void> entity = new HttpEntity<>(headers);
ResponseEntity<Map> response = restTemplate.exchange(
    builder.toUriString(), 
    HttpMethod.GET, 
    entity, 
    Map.class
);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'config-status':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Configuration Status
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Check the setup status of domains associated with a customer. This endpoint provides
                            detailed configuration states for domains, supporting pagination and sorting.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/records/view"
                            description="Get domain configuration status"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "customerId", type: "long", required: true, description: "Customer ID" },
                                { name: "page", type: "int", required: false, description: "Zero-based page index (0..N)" },
                                { name: "size", type: "int", required: false, description: "The size of the page to be returned (default: 20)" },
                                { name: "sort", type: "string", required: false, description: "Sorting criteria (e.g., domainName,asc)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "content": [
                                        {
                                            "domainName": "inboxadsuite.com",
                                            "status": "CREATED",
                                            "dkimStatus": "NONE",
                                            "spfStatus": "NONE",
                                            "dmarcStatus": "NONE"
                                        }
                                    ],
                                    "pageable": {
                                        "sort": { "empty": false, "sorted": true, "unsorted": false },
                                        "offset": 0,
                                        "pageNumber": 0,
                                        "pageSize": 20,
                                        "paged": true,
                                        "unpaged": false
                                    },
                                    "totalElements": 1,
                                    "totalPages": 1,
                                    "last": true,
                                    "size": 20,
                                    "number": 0,
                                    "sort": { "empty": false, "sorted": true, "unsorted": false },
                                    "numberOfElements": 1,
                                    "first": true,
                                    "empty": false
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/records/view?customerId=123&page=0&size=20" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`// Using Spring RestTemplate with Bearer Token
@Value("${"spring.baseUrl"}")
private String baseUrl;

RestTemplate restTemplate = new RestTemplate();
String token = "your_jwt_token_here";

// Build URL with query parameters
UriComponentsBuilder builder = UriComponentsBuilder
    .fromHttpUrl(baseUrl + "/records/view")
    .queryParam("customerId", 123)
    .queryParam("page", 0)
    .queryParam("size", 20);

String url = builder.toUriString();

// Set headers with Bearer token
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth(token);
HttpEntity<Void> entity = new HttpEntity<>(headers);

// Make GET request
ResponseEntity<Page<Domains>> response = restTemplate.exchange(
    url,
    HttpMethod.GET,
    entity,
    new ParameterizedTypeReference<Page<Domains>>() {}
);

Page<Domains> domainPage = response.getBody();
System.out.println("Total Domains: " + domainPage.getTotalElements());`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'update-from-name':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                10. Update "From Name"
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Update the sender display (From Name) for specific products within an order.
                        </Typography>
                        <StructuredApiEndpoint
                            method="PUT"
                            path="/{orderNumber}/from-name"
                            description="Update display name for mailboxes"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "orderNumber", type: "long", required: true, description: "The order number (Path parameter)" }
                            ]}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: {
                                        "updateProducts": [
                                            {
                                                "productId": "abc123",
                                                "firstName": "John",
                                                "lastName": "Doe"
                                            }
                                        ]
                                    }
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: { "message": "Updated successfully" }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X PUT "https://api.inboxlogy.com/12345/from-name" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{
  "updateProducts": [
    {
      "productId": "abc123",
      "firstName": "John",
      "lastName": "Doe"
    }
  ]
}'`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");

Map<String, Object> request = new HashMap<>();
List<Map<String, String>> products = new ArrayList<>();
Map<String, String> p1 = new HashMap<>();
p1.put("productId", "689b009...");
p1.put("firstName", "John");
p1.put("lastName", "Doe");
products.add(p1);
request.put("updateProducts", products);

HttpEntity<Map> entity = new HttpEntity<>(request, headers);
restTemplate.put(baseUrl + "/" + orderNumber + "/from-name", entity);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'update-profile-pic':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Update Profile Picture
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Update profile pictures for specific mailboxes within an order. This endpoint uses multipart form data to handle both JSON metadata and binary image files.
                        </Typography>
                        <StructuredApiEndpoint
                            method="PUT"
                            path="/{orderNumber}/profile-picture"
                            description="Upload profile pictures for products using multipart/form-data"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "orderNumber", type: "long", required: true, description: "The order number (Path parameter)" }
                            ]}
                            bodyParams={{
                                contentType: "multipart/form-data",
                                schema: {
                                    description: "Multipart request parts",
                                    example: {
                                        "updateProducts": '[{"productId":"689b0...19c0"}]',
                                        "files": "[Binary File(s)]"
                                    }
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: { "message": "Profile picture updated successfully" }
                            }]}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
                                Form Data Parts
                            </Typography>
                            <Table size="small" sx={{ mb: 3, border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Part Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: 'monospace', color: '#3b82f6' }}>updateProducts</TableCell>
                                        <TableCell>String (JSON)</TableCell>
                                        <TableCell>A JSON array string containing product identifiers: <code>[{`{"productId": "..."}`}]</code></TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: 'monospace', color: '#3b82f6' }}>files</TableCell>
                                        <TableCell>MultipartFile[]</TableCell>
                                        <TableCell>List of binary image files to upload.</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X PUT "https://api.inboxlogy.com/12345/profile-picture" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -F 'updateProducts=[{"productId":"689b0...19c0"}]' \\
     -F "files=@/path/to/image.png"`} />
                            <CodeBlock title="Java Example" language="java" code={`@PutMapping(value = "/{orderNumber}/profile-picture", consumes = MediaType.MULTIPART_FORM_DATA_VALUE)
public void updateProfilePicture(
        @PathVariable Long orderNumber,
        @RequestPart("updateProducts") String updateProductsJson,
        @RequestPart("files") List<MultipartFile> files) throws IOException {

    List<ProductProfilePictureUpdateItem> updateProducts =
            objectMapper.readValue(updateProductsJson,
                    new TypeReference<>() {});

    productUpdateService.updateProfilePicture(orderNumber, updateProducts, files);
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'mailbox-credentials':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Get Mailbox Credentials
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Retrieve login credentials (password and login URL) for a specific mailbox associated with an order.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/{orderNumber}/mailbox-credentials"
                            description="Retrieve mailbox password and login URL"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "orderNumber", type: "long", required: true, description: "The unique ID of the order (Path parameter)" },
                                { name: "email", type: "string", required: true, description: "The email address of the mailbox (Query parameter)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "email": "user@example.com",
                                    "password": "hashed_password_or_temporary_token",
                                    "loginUrl": "https://mail.google.com",
                                    "status": "ACTIVE"
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/12345/mailbox-credentials?email=user@example.com" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`@GetMapping("/{orderNumber}/mailbox-credentials")
public MailboxCredentialsResponse getMailboxCredentials(
        @PathVariable Long orderNumber,
        @RequestParam String email) {

    return productUpdateService.getMailboxCredentials(orderNumber, email);
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'get-dns':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Management (DNS Records) - Get DNS Records
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            List all DNS records for a given domain managed through Cloudflare.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/api/cloudflare/dns-records"
                            description="Fetch DNS records for a domain"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "domain", type: "string", required: true, description: "Domain name (e.g., thedabbahwala.info)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: [
                                    {
                                        "id": "a249fb8a50c7f50bc736462e3e284426",
                                        "type": "A",
                                        "name": "thedabbahwala.info",
                                        "content": "192.0.2.1",
                                        "proxied": true,
                                        "ttl": 1,
                                        "priority": null
                                    }
                                ]
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/api/cloudflare/dns-records?domain=example.com" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");

UriComponentsBuilder builder = UriComponentsBuilder
    .fromHttpUrl(baseUrl + "/api/cloudflare/dns-records")
    .queryParam("domain", "example.com");

HttpEntity<Void> entity = new HttpEntity<>(headers);
ResponseEntity<List> response = restTemplate.exchange(
    builder.toUriString(), 
    HttpMethod.GET, 
    entity, 
    List.class
);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'update-dns':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Management (DNS Records) - Update DNS Records (Bulk)
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Create or update multiple DNS records in bulk for a target domain. This endpoint
                            efficiently processes a list of DNS records (A, CNAME, etc.) in a single request.
                        </Typography>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/api/cloudflare/zones/{domainName}/dns-records/bulk"
                            description="Bulk update DNS records"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "domainName", type: "string", required: true, description: "The name of the domain to update (Path parameter)" }
                            ]}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: [
                                        {
                                            "type": "CNAME",
                                            "name": "www",
                                            "content": "target.com",
                                            "ttl": 120
                                        }
                                    ]
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: [
                                    {
                                        "request": {
                                            "type": "CNAME",
                                            "name": "www.thedabbahwala.info",
                                            "content": "target.com",
                                            "ttl": 120,
                                            "proxied": null,
                                            "priority": null,
                                            "comment": null
                                        },
                                        "status": "SUCCESS",
                                        "message": "Record created"
                                    }
                                ]
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/api/cloudflare/zones/example.com/dns-records/bulk" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '[
  {
    "type": "CNAME",
    "name": "www",
    "content": "target.com",
    "ttl": 120
  }
]'`} />
                            <CodeBlock title="Java Example" language="java" code={`List<Map<String, Object>> dnsRequests = new ArrayList<>();
Map<String, Object> record = new HashMap<>();
record.put("type", "CNAME");
record.put("name", "app");
record.put("content", "myapp.com");
record.put("ttl", 120);
dnsRequests.add(record);

RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");
HttpEntity<List<Map<String, Object>>> entity = new HttpEntity<>(dnsRequests, headers);

String url = baseUrl + "/api/cloudflare/zones/" + domainName + "/dns-records/bulk";
ResponseEntity<List> response = restTemplate.postForEntity(url, entity, List.class);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'create-redirect':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Forwarding (Redirect) - Create Redirect
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Set up a redirect (forwarding) for a domain zone.
                        </Typography>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/api/cloudflare/zones/{zoneId}/domain-forward"
                            description="Create a domain redirect"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "zoneId", type: "string", required: true, description: "The domain name (e.g., example.com)" },
                                { name: "sourceDomain", type: "string", required: true, description: "Source domain" },
                                { name: "targetDomain", type: "string", required: true, description: "Target domain" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: { "status": "SUCCESS" }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X POST "https://api.inboxlogy.com/api/cloudflare/zones/example.com/domain-forward?sourceDomain=old.com&targetDomain=new.com" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`UriComponentsBuilder builder = UriComponentsBuilder
    .fromHttpUrl(baseUrl + "/api/cloudflare/zones/" + zoneId + "/domain-forward")
    .queryParam("sourceDomain", "old.com")
    .queryParam("targetDomain", "new.com");

restTemplate.postForEntity(builder.toUriString(), entity, Map.class);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'delete-redirect':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Forwarding (Redirect) - Delete Redirect
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Remove an existing domain redirect.
                        </Typography>
                        <StructuredApiEndpoint
                            method="DELETE"
                            path="/api/cloudflare/zones/{zoneId}/redirect"
                            description="Remove domain forwarding"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryPams={[
                                { name: "zoneId", type: "string", required: true, description: "The domain name (e.g., example.com)" }
                            ]}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    example: {
                                        "sourceDomain": "example.com",
                                        "reason": "Campaign ended, no longer need redirect"
                                    }
                                }
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "status": 200,
                                    "message": "Domain forwarding removed successfully",
                                    "data": {
                                        "zoneId": "abc123",
                                        "sourceDomain": "example.com",
                                        "action": "REDIRECT_REMOVED"
                                    }
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X DELETE "https://api.inboxlogy.com/api/cloudflare/zones/example.com/redirect" \\
     -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     -d '{
  "sourceDomain": "example.com",
  "reason": "Campaign ended, no longer need redirect"
}'`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");
headers.setContentType(MediaType.APPLICATION_JSON);

Map<String, String> request = new HashMap<>();
request.put("sourceDomain", "example.com");
request.put("reason", "Campaign ended");
HttpEntity<Map> entity = new HttpEntity<>(request, headers);

restTemplate.exchange(
    baseUrl + "/api/cloudflare/zones/" + zoneId + "/redirect", 
    HttpMethod.DELETE, 
    entity, 
    Map.class
);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'renewal-pricing':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Domain Renewal Pricing
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Get the registration and renewal pricing for domains.
                        </Typography>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/domain/list"
                            description="Fetch domain price list"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: [
                                    {
                                        "domainName": "inboxlogy.com",
                                        "registerPrice": 10.1,
                                        "renewPrice": 20.08
                                    }
                                ]
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/domain/list" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`RestTemplate restTemplate = new RestTemplate();
HttpHeaders headers = new HttpHeaders();
headers.setBearerAuth("YOUR_TOKEN");

HttpEntity<Void> entity = new HttpEntity<>(headers);
ResponseEntity<List> response = restTemplate.exchange(
    baseUrl + "/domain/list", 
    HttpMethod.GET, 
    entity, 
    List.class
);`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'email-forwarding':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                Email Forwarding
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Management of email forwarding rules.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ p: 3, backgroundColor: '#f3f4f6', borderRadius: '12px', borderLeft: '4px solid #6b7280' }}>
                                <Typography variant="body1" sx={{ color: '#374151', fontWeight: 600 }}>
                                    - Create → Handled by Nishikant
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3, backgroundColor: '#f3f4f6', borderRadius: '12px', borderLeft: '4px solid #6b7280' }}>
                                <Typography variant="body1" sx={{ color: '#374151', fontWeight: 600 }}>
                                    - Delete → Handled by Nishikant
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                );

            case 'oauth2-integration':
                return (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
                                OAuth2 Mailbox Integration
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Integration with mailboxes using OAuth2 authentication.
                        </Typography>
                        <Box sx={{ p: 3, backgroundColor: '#f3f4f6', borderRadius: '12px', borderLeft: '4px solid #6b7280' }}>
                            <Typography variant="body1" sx={{ color: '#374151', fontWeight: 600 }}>
                                Handled by Puneet
                            </Typography>
                        </Box>
                    </Box>
                );

            case 'order-status-check':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            Order Status API
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Check the current status of a specific order using its order number. This endpoint is primarily used as a webhook for UMS team order updates, providing real-time tracking of order progress.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Authorization
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
                                This endpoint requires a valid <strong>Bearer Token</strong> in the <code>Authorization</code> header.
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/order-status/{orderNumber}"
                            description="Get the status of a specific order by order number"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "orderNumber", type: "long", required: true, description: "The order number to check (Path parameter)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "orderNumber": 123456789,
                                    "status": "COMPLETED",
                                    "message": "Order status fetched successfully",
                                    "modifiedDate": "2025-01-07T10:34:00Z"
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/order-status/123456789" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />
                            <CodeBlock title="Java Example" language="java" code={`/**
 * Webhook for UMS team order update
 */
@GetMapping("/order-status/{orderNumber}")
public ResponseEntity<OrderStatusResponseDTO> getOrderStatus(
        HttpServletRequest request, 
        @PathVariable long orderNumber) {

    String userData = loginService.getUserFromToken(request);
    return orderService.getOrderStatusByOrderNumber(request, userData, orderNumber);
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'view-flatten-order':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            View Flatten Order API
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Retrieve detailed information about a specific order in a flattened structure. This endpoint is useful for
                            getting a comprehensive view of all products and their configurations within a single order.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Authorization
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
                                A valid <strong>Bearer Token</strong> is required in the Authorization header.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                Example: Authorization: Bearer ********************
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="GET"
                            path="/flatten/vieworderbyorderid"
                            description="Retrieve detailed order information including nested product lists and flattened order summaries. This endpoint provides a comprehensive view of the entire order structure, including status, products, and summary statistics."
                            authorization={{
                                description: "A valid Bearer Token is required in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            queryParams={[
                                { name: "orderid", type: "string", required: true, description: "The unique identifier of the order (e.g., 69df5d26f56fbd5d811b9094)" }
                            ]}
                            responseExamples={[{
                                status: 200,
                                statusText: "OK",
                                example: {
                                    "orders": [
                                        {
                                            "productDTOList": [
                                                {
                                                    "productId": "69df2c13d916dc226817d779",
                                                    "productNumber": 0,
                                                    "customerId": 0,
                                                    "orderId": null,
                                                    "orderNumber": 0,
                                                    "organisationName": "Prewarm - clipo-ai.app",
                                                    "emailProvider": "GOOGLE WORKSPACE",
                                                    "domain": "test76.com",
                                                    "email": "test1@test76.com",
                                                    "firstName": "Vihaan",
                                                    "lastName": "Iyer",
                                                    "profilePicture": "https://randomuser.me/api/portraits/men/34.jpg",
                                                    "fwdDomain": "INBOX.com",
                                                    "preferredPassword": "9M.G2w}xf?",
                                                    "recoveryEmail": "setupteam@demandlogy.com",
                                                    "status": "ACTIVE",
                                                    "emailSendingTool": null,
                                                    "workspace": null,
                                                    "loginEmail": null,
                                                    "loginPassword": null,
                                                    "domainRegistrarURL": null,
                                                    "domainRegistrarEmail": null,
                                                    "domainRegistrarPassword": null,
                                                    "domainRegistrar2FAFlag": null,
                                                    "workspaceAdminFlag": true,
                                                    "emailFwdFlag": null,
                                                    "fwdEmailAddress": null,
                                                    "fwdMailBoxLoginURL": null,
                                                    "fwdMailBoxLogin": null,
                                                    "fwdMailBoxLoginPassword": null,
                                                    "fwdMailBox2FAFlag": null,
                                                    "isActive": null,
                                                    "createdDate": "2026-04-15T06:11:31.457+00:00",
                                                    "modifiedDate": "2026-04-20T06:58:49.127+00:00"
                                                },
                                                {
                                                    "productId": "69df2c13d916dc226817d77a",
                                                    "productNumber": 0,
                                                    "customerId": 0,
                                                    "orderId": null,
                                                    "orderNumber": 0,
                                                    "organisationName": "Prewarm - clipo-ai.app",
                                                    "emailProvider": "GOOGLE WORKSPACE",
                                                    "domain": "test76.com",
                                                    "email": "test2@test76.com",
                                                    "firstName": "Rohan",
                                                    "lastName": "Singh",
                                                    "profilePicture": "https://randomuser.me/api/portraits/women/35.jpg",
                                                    "fwdDomain": "INBOX.com",
                                                    "preferredPassword": "PDSDB7Ruq@",
                                                    "recoveryEmail": "setupteam@demandlogy.com",
                                                    "status": "ACTIVE",
                                                    "emailSendingTool": null,
                                                    "workspace": null,
                                                    "loginEmail": null,
                                                    "loginPassword": null,
                                                    "domainRegistrarURL": null,
                                                    "domainRegistrarEmail": null,
                                                    "domainRegistrarPassword": null,
                                                    "domainRegistrar2FAFlag": null,
                                                    "workspaceAdminFlag": false,
                                                    "emailFwdFlag": null,
                                                    "fwdEmailAddress": null,
                                                    "fwdMailBoxLoginURL": null,
                                                    "fwdMailBoxLogin": null,
                                                    "fwdMailBoxLoginPassword": null,
                                                    "fwdMailBox2FAFlag": null,
                                                    "isActive": null,
                                                    "createdDate": "2026-04-15T06:11:31.460+00:00",
                                                    "modifiedDate": "2026-04-20T06:58:49.127+00:00"
                                                },
                                                {
                                                    "productId": "69df2c13d916dc226817d77b",
                                                    "productNumber": 0,
                                                    "customerId": 0,
                                                    "orderId": null,
                                                    "orderNumber": 0,
                                                    "organisationName": "Prewarm - clipo-ai.app",
                                                    "emailProvider": "GOOGLE WORKSPACE",
                                                    "domain": "test76.com",
                                                    "email": "test3@test76.com",
                                                    "firstName": "Aarav",
                                                    "lastName": "Sharma",
                                                    "profilePicture": "https://randomuser.me/api/portraits/women/36.jpg",
                                                    "fwdDomain": "INBOX.com",
                                                    "preferredPassword": "yZgH^r9Vfu",
                                                    "recoveryEmail": "setupteam@demandlogy.com",
                                                    "status": "ACTIVE",
                                                    "emailSendingTool": null,
                                                    "workspace": null,
                                                    "loginEmail": null,
                                                    "loginPassword": null,
                                                    "domainRegistrarURL": null,
                                                    "domainRegistrarEmail": null,
                                                    "domainRegistrarPassword": null,
                                                    "domainRegistrar2FAFlag": null,
                                                    "workspaceAdminFlag": false,
                                                    "emailFwdFlag": null,
                                                    "fwdEmailAddress": null,
                                                    "fwdMailBoxLoginURL": null,
                                                    "fwdMailBoxLogin": null,
                                                    "fwdMailBoxLoginPassword": null,
                                                    "fwdMailBox2FAFlag": null,
                                                    "isActive": null,
                                                    "createdDate": "2026-04-15T06:11:31.462+00:00",
                                                    "modifiedDate": "2026-04-20T06:58:49.127+00:00"
                                                }
                                            ],
                                            "orderId": "69df2c13d916dc226817d77c",
                                            "customerId": 90,
                                            "orderNumber": "INB3437",
                                            "organisationName": "Prewarm - clipo-ai.app",
                                            "orderDate": "15-04-2026",
                                            "userId": "6889aac5c9096436101d0907",
                                            "userName": "faizan@demandlogy.com",
                                            "activeStep": 1,
                                            "status": "LICENSING",
                                            "internalStatus": null,
                                            "isActive": true,
                                            "createdDate": "2026-04-15T06:11:31.548+00:00",
                                            "modifiedDate": "2026-04-20T06:58:49.127+00:00",
                                            "summary": null,
                                            "orderStatusHistory": null,
                                            "orderType": null
                                        }
                                    ],
                                    "flatOrders": [
                                        {
                                            "customerId": "CID90",
                                            "orderId": "69df2c13d916dc226817d77c",
                                            "orderNumber": "INB3437",
                                            "orderDate": "15-04-2026",
                                            "userId": "6889aac5c9096436101d0907",
                                            "userName": "faizan@demandlogy.com",
                                            "productId": "69df2c13d916dc226817d779",
                                            "productName": null,
                                            "organisationName": "Prewarm - clipo-ai.app",
                                            "emailProvider": "GOOGLE WORKSPACE",
                                            "domain": "test76.com",
                                            "email": "test1@test76.com",
                                            "firstName": "Vihaan",
                                            "lastName": "Iyer",
                                            "profilePicture": "https://randomuser.me/api/portraits/men/34.jpg",
                                            "fwdDomain": "INBOX.com",
                                            "preferredPassword": "9M.G2w}xf?",
                                            "recoveryEmail": "setupteam@demandlogy.com",
                                            "emailSendingTool": null,
                                            "workspace": null,
                                            "loginEmail": null,
                                            "loginPassword": null,
                                            "domainRegistrarURL": null,
                                            "domainRegistrarEmail": null,
                                            "domainRegistrarPassword": null,
                                            "domainRegistrar2FAFlag": null,
                                            "workspaceAdminFlag": true,
                                            "emailFwdFlag": null,
                                            "fwdEmailAddress": null,
                                            "fwdMailBoxLoginURL": null,
                                            "fwdMailBoxLogin": null,
                                            "fwdMailBoxLoginPassword": null,
                                            "fwdMailBox2FAFlag": null,
                                            "isActive": null,
                                            "createdDate": "2026-04-15T06:11:31.457+00:00",
                                            "modifiedDate": "2026-04-15T06:11:31.457+00:00",
                                            "userType": "User",
                                            "status": "ACTIVE",
                                            "productNumber": 2415
                                        },
                                        {
                                            "customerId": "CID90",
                                            "orderId": "69df2c13d916dc226817d77c",
                                            "orderNumber": "INB3437",
                                            "orderDate": "15-04-2026",
                                            "userId": "6889aac5c9096436101d0907",
                                            "userName": "faizan@demandlogy.com",
                                            "productId": "69df2c13d916dc226817d77a",
                                            "productName": null,
                                            "organisationName": "Prewarm - clipo-ai.app",
                                            "emailProvider": "GOOGLE WORKSPACE",
                                            "domain": "test76.com",
                                            "email": "test2@test76.com",
                                            "firstName": "Rohan",
                                            "lastName": "Singh",
                                            "profilePicture": "https://randomuser.me/api/portraits/women/35.jpg",
                                            "fwdDomain": "INBOX.com",
                                            "preferredPassword": "PDSDB7Ruq@",
                                            "recoveryEmail": "setupteam@demandlogy.com",
                                            "emailSendingTool": null,
                                            "workspace": null,
                                            "loginEmail": null,
                                            "loginPassword": null,
                                            "domainRegistrarURL": null,
                                            "domainRegistrarEmail": null,
                                            "domainRegistrarPassword": null,
                                            "domainRegistrar2FAFlag": null,
                                            "workspaceAdminFlag": false,
                                            "emailFwdFlag": null,
                                            "fwdEmailAddress": null,
                                            "fwdMailBoxLoginURL": null,
                                            "fwdMailBoxLogin": null,
                                            "fwdMailBoxLoginPassword": null,
                                            "fwdMailBox2FAFlag": null,
                                            "isActive": null,
                                            "createdDate": "2026-04-15T06:11:31.460+00:00",
                                            "modifiedDate": "2026-04-15T06:11:31.460+00:00",
                                            "userType": "User",
                                            "status": "ACTIVE",
                                            "productNumber": 2416
                                        },
                                        {
                                            "customerId": "CID90",
                                            "orderId": "69df2c13d916dc226817d77c",
                                            "orderNumber": "INB3437",
                                            "orderDate": "15-04-2026",
                                            "userId": "6889aac5c9096436101d0907",
                                            "userName": "faizan@demandlogy.com",
                                            "productId": "69df2c13d916dc226817d77b",
                                            "productName": null,
                                            "organisationName": "Prewarm - clipo-ai.app",
                                            "emailProvider": "GOOGLE WORKSPACE",
                                            "domain": "test76.com",
                                            "email": "test3@test76.com",
                                            "firstName": "Aarav",
                                            "lastName": "Sharma",
                                            "profilePicture": "https://randomuser.me/api/portraits/women/36.jpg",
                                            "fwdDomain": "INBOX.com",
                                            "preferredPassword": "yZgH^r9Vfu",
                                            "recoveryEmail": "setupteam@demandlogy.com",
                                            "emailSendingTool": null,
                                            "workspace": null,
                                            "loginEmail": null,
                                            "loginPassword": null,
                                            "domainRegistrarURL": null,
                                            "domainRegistrarEmail": null,
                                            "domainRegistrarPassword": null,
                                            "domainRegistrar2FAFlag": null,
                                            "workspaceAdminFlag": false,
                                            "emailFwdFlag": null,
                                            "fwdEmailAddress": null,
                                            "fwdMailBoxLoginURL": null,
                                            "fwdMailBoxLogin": null,
                                            "fwdMailBoxLoginPassword": null,
                                            "fwdMailBox2FAFlag": null,
                                            "isActive": null,
                                            "createdDate": "2026-04-15T06:11:31.462+00:00",
                                            "modifiedDate": "2026-04-15T06:11:31.462+00:00",
                                            "userType": "User",
                                            "status": "ACTIVE",
                                            "productNumber": 2417
                                        }
                                    ],
                                    "summary": {
                                        "productSummaryList": [
                                            {
                                                "productName": "GOOGLE WORKSPACE",
                                                "quantity": 3,
                                                "domainQuantity": 1,
                                                "active": 3,
                                                "cancelled": 0,
                                                "pendingRenewal": 0,
                                                "completed": 0,
                                                "draft": 0,
                                                "setupInProgress": 0,
                                                "paymentFailed": 0,
                                                "paymentPending": 0
                                            }
                                        ],
                                        "totalEmail": 3
                                    },
                                    "totalCount": 0,
                                    "orderCountByStatus": 0,
                                    "paymentSummery": null,
                                    "domainProviderCredentialNumber": 0
                                }
                            }]}
                        >
                            <CodeBlock title="cURL (Postman) Example" language="bash" code={`curl -X GET "https://api.inboxlogy.com/flatten/vieworderbyorderid?orderid=69df5d26f56fbd5d811b9094" \\
     -H "Authorization: Bearer YOUR_TOKEN"`} />

                            <CodeBlock title="Java Example" language="java" code={`// Using Spring RestTemplate
@Value("${"spring.baseUrl"}")
private String baseUrl;

public void viewFlattenOrder(String orderId) {
    RestTemplate restTemplate = new RestTemplate();
    HttpHeaders headers = new HttpHeaders();
    headers.setBearerAuth("YOUR_TOKEN");

    UriComponentsBuilder builder = UriComponentsBuilder
        .fromHttpUrl(baseUrl + "/flatten/vieworderbyorderid")
        .queryParam("orderid", orderId);

    HttpEntity<Void> entity = new HttpEntity<>(headers);
    ResponseEntity<Map> response = restTemplate.exchange(
        builder.toUriString(), 
        HttpMethod.GET, 
        entity, 
        Map.class
    );
    
    System.out.println("Order Data: " + response.getBody());
}`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            case 'cancelsubscription':
                return (
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1f2937' }}>
                            Cancel Subscription API
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#6b7280', lineHeight: 1.8 }}>
                            Cancel an active email account subscription. This endpoint allows you to terminate a mailbox subscription
                            and stop all recurring charges associated with the email account. Once cancelled, the email account will be
                            deactivated and marked for deletion according to your service agreement terms.
                        </Typography>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                                Authorization
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 1 }}>
                                This endpoint requires a valid <strong>Bearer Token</strong> in the <code>Authorization</code> header.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4b5563', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                Example: Authorization: Bearer ********************
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#991b1b', mb: 1 }}>
                                ⚠️ Warning
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                                Cancelling a subscription is irreversible. Make sure you have backed up any important data from the email account before proceeding.
                            </Typography>
                        </Box>
                        <StructuredApiEndpoint
                            method="POST"
                            path="/cancelsubscription"
                            description="Cancel an active email account subscription"
                            authorization={{
                                description: "This endpoint requires a valid Bearer Token in the Authorization header.",
                                example: "Authorization: Bearer ********************"
                            }}
                            headerParams={[
                                {
                                    name: "Authorization",
                                    type: "string",
                                    required: true,
                                    description: "Bearer token for authentication"
                                },
                                {
                                    name: "Content-Type",
                                    type: "string",
                                    required: true,
                                    description: "Content type of the request body"
                                }
                            ]}
                            bodyParams={{
                                contentType: "application/json",
                                schema: {
                                    description: "Subscription cancellation details",
                                    example: {
                                        "orders": [
                                            { "orderId": "ord_12345" }
                                        ],
                                        "products": [
                                            { "productId": "prod_12345" }
                                        ],
                                        "reason": "No longer needed"
                                    }
                                }
                            }}
                            responseExamples={[
                                {
                                    status: 200,
                                    statusText: "OK",
                                    example: {
                                        "status": "success",
                                        "message": "Subscription cancelled successfully",
                                        "email": "user@domain.com",
                                        "cancellationDate": "2025-01-07T10:30:00Z"
                                    }
                                },
                                {
                                    status: 404,
                                    statusText: "Not Found",
                                    example: {
                                        "status": "error",
                                        "message": "Subscription not found or already cancelled"
                                    }
                                }
                            ]}
                        >
                            <CodeBlock title="Request Payload" language="json" code={JSON.stringify({
                                "orders": [
                                    { "orderId": "ord_12345" }
                                ],
                                "products": [
                                    { "productId": "prod_12345" }
                                ],
                                "reason": "No longer needed"
                            }, null, 2)} />
                            <CodeBlock title="Java Example" language="java" code={`// Using Spring RestTemplate to Cancel Subscription
@Value("${"spring.baseUrl"}")
private String baseUrl;

RestTemplate restTemplate = new RestTemplate();
String url = baseUrl + "/cancelsubscription";
String token = "your_jwt_token_here";

// Create cancellation request
Map<String, Object> requestBody = new HashMap<>();

// Add orders
List<Map<String, String>> orders = new ArrayList<>();
Map<String, String> order = new HashMap<>();
order.put("orderId", "ord_12345");
orders.add(order);

// Add products
List<Map<String, String>> products = new ArrayList<>();
Map<String, String> product = new HashMap<>();
product.put("productId", "prod_12345");
products.add(product);

requestBody.put("orders", orders);
requestBody.put("products", products);
requestBody.put("reason", "No longer needed");

// Set headers with Bearer token
HttpHeaders headers = new HttpHeaders();
headers.setContentType(MediaType.APPLICATION_JSON);
headers.setBearerAuth(token);
HttpEntity<Map<String, Object>> entity = new HttpEntity<>(requestBody, headers);

// Make POST request
ResponseEntity<CancellationResponse> response = restTemplate.postForEntity(
    url, 
    entity, 
    CancellationResponse.class
);

// Get cancellation status
CancellationResponse result = response.getBody();
System.out.println("Status: " + result.getStatus());
System.out.println("Message: " + result.getMessage());`} />
                        </StructuredApiEndpoint>
                    </Box>
                );

            default:
                return (
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Welcome to Inboxlogy API
                        </Typography>
                        <Typography variant="body1">
                            Select an endpoint from the sidebar to view its documentation.
                        </Typography>
                    </Box>
                );
        }
    };

    return (
        <Box sx={{
            flex: 1,
            p: 4,
            overflowY: 'auto',
            ml: '300px',
            minHeight: '100vh',
            backgroundColor: '#fff',
            width: 'calc(100% - 300px)'
        }}>
            {renderContent()}
        </Box>
    );
};

export default MainContent;
